import { ActionButton, ActionButtonType, Button } from "@octopusdeploy/design-system-components";
import { useInlineStatusQuery } from "@octopusdeploy/octopus-react-client";
import type { ArgoCDGatewayHealthStatus, ProjectResource } from "@octopusdeploy/octopus-server-client";
import { ActionHandlerCategory, HostingEnvironment } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import type { ArgoCDInstanceConfiguration } from "~/areas/infrastructure/components/ArgoCDInstances/ArgoCDInstanceConfiguration";
import { ConfigurationView } from "~/areas/infrastructure/components/ArgoCDInstances/DialogViews/ConfigurationView";
import { InstallationStatusView } from "~/areas/infrastructure/components/ArgoCDInstances/DialogViews/InstallationStatusView";
import { InstallationView } from "~/areas/infrastructure/components/ArgoCDInstances/DialogViews/InstallationView";
import { ProjectSelectionView } from "~/areas/infrastructure/components/ArgoCDInstances/DialogViews/ProjectSelectionView";
import { ConfigurationViewHelpContent, ConfigurationViewHelpImage, InstallationViewHelpContent, InstallationViewHelpImage, ProjectSelectionViewHelpContent, ProjectSelectionViewHelpImage, } from "~/areas/infrastructure/components/ArgoCDInstances/DialogViews/components/AddDialogHelp";
import { configurationToRegistrationEvent, validateConfiguration } from "~/areas/infrastructure/components/ArgoCDInstances/configurationValidation";
import { useArgoCDProjectSelectionDispatcher } from "~/areas/infrastructure/components/ArgoCDInstances/useArgoCDProjectSelectionDispatcher";
import { useBearerToken } from "~/areas/infrastructure/components/ArgoCDInstances/useBearerToken";
import { useValidatingModel } from "~/areas/infrastructure/components/ArgoCDInstances/useValidatingModel";
import { getServerGrpcUri } from "~/areas/infrastructure/components/MachineSettings/Endpoints/KubernetesAgent/configurationUtils";
import { initialProjectStatusState, projectStatusReducer } from "~/areas/projects/components/ProjectStatus/reducer";
import { client } from "~/clientInstance";
import { useLegacyDoBusyTask } from "~/components/DataBaseComponent/useLegacyDoBusyTask";
import { useSpaceAwareNavigation } from "~/components/Navigation/SpaceAwareNavigation/useSpaceAwareNavigation";
import type { PagedOnboardingDialogPageData, PageNavigation } from "~/components/OnboardingDialog/PagedOnboardingDialog";
import { PagedOnboardingDialog } from "~/components/OnboardingDialog/PagedOnboardingDialog";
import NewProjectWizard from "~/components/ProjectBasedActivation/NewProjectWizard";
import { useArgoCDRegistrationPageAnalyticsDispatcher } from "./useArgoCDRegistrationPageAnalyticsDispatcher";
type BaseDialogProps = {
    open: boolean;
    onClose: (wasInstalled: boolean) => void;
    spaceId: string;
    showProjectSelection: boolean;
};
type GuidedSetupDialogProps = BaseDialogProps & {
    isGuidedSetup: true;
    onInstanceConfigurationChanged: (configuration: ArgoCDInstanceConfiguration) => void;
    onBearerTokenExpired: () => void;
};
export type FoundGatewayInstance = {
    InstanceId: string;
    GatewayId: string;
    SpaceId: string;
    HealthStatus: ArgoCDGatewayHealthStatus;
};
function isGuidedSetupProps(props: AddArgoCDInstanceDialogProps): props is GuidedSetupDialogProps {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    return (props as GuidedSetupDialogProps).isGuidedSetup;
}
type AddArgoCDInstanceDialogProps = BaseDialogProps | GuidedSetupDialogProps;
export const defaultArgoDNSUrl = "argocd-server.argocd.svc.cluster.local";
export const AddArgoCDInstanceDialog = (props: AddArgoCDInstanceDialogProps) => {
    const { open, onClose, spaceId, showProjectSelection } = props;
    const isGuidedSetup = isGuidedSetupProps(props);
    const onInstanceConfigurationChanged = isGuidedSetup ? props.onInstanceConfigurationChanged : undefined;
    const onBearerTokenExpired = isGuidedSetup ? props.onBearerTokenExpired : undefined;
    const [foundGateway, setFoundGateway] = useState<FoundGatewayInstance>();
    const isGatewayFound = !!foundGateway;
    const [isWaitingForGatewayConnection, setIsWaitingForGatewayConnection] = useState<boolean>(false);
    const [hasAutoNavigatedToStatus, setHasAutoNavigatedToStatus] = useState<boolean>(false);
    const [useExistingProject, setUseExistingProject] = useState(true);
    const [selectedProjectSlug, setSelectedProjectSlug] = useState<string | undefined>();
    const [showNewProjectWizard, setShowNewProjectWizard] = useState(false);
    const { doBusyTask } = useLegacyDoBusyTask();
    const [projectStatusState, projectStatusDispatch] = useReducer(projectStatusReducer, initialProjectStatusState);
    const bearerToken = useBearerToken();
    const argoCDRegistrationPageAnalyticsDispatcher = useArgoCDRegistrationPageAnalyticsDispatcher();
    const dispatchProjectSelection = useArgoCDProjectSelectionDispatcher();
    const { navigate } = useSpaceAwareNavigation();
    const dialogRef = useRef<PageNavigation>(null);
    const { model, setModel, validate, getFieldError, reset } = useValidatingModel<ArgoCDInstanceConfiguration>({
        initialModel: {
            name: "",
            argoCDUrl: defaultArgoDNSUrl,
            argoCDWebUrl: "",
            argoCDAuthenticationToken: "",
            environmentIds: [],
            environmentSlugs: [],
            octopusServerUrl: client.resolve("/"),
            octopusGrpcUrl: getServerGrpcUri(),
            spaceId: spaceId,
            argoAllowUnsignedCertificate: true,
        },
        onValidate: async (repo, modelToValidate) => {
            const errors = await validateConfiguration(repo, modelToValidate);
            const analyticsEvent = configurationToRegistrationEvent(modelToValidate);
            if (Object.keys(errors).length == 0) {
                argoCDRegistrationPageAnalyticsDispatcher("Submit Argo CD Gateway Register Page", analyticsEvent);
            }
            else {
                argoCDRegistrationPageAnalyticsDispatcher("Submit Argo CD Gateway Register Page", { ...analyticsEvent, validationErrors: true });
            }
            return errors;
        },
    });
    useInlineStatusQuery(async (repo) => {
        if (!isWaitingForGatewayConnection || (isGatewayFound && foundGateway?.HealthStatus !== "Unknown")) {
            return;
        }
        const instanceSummaries = await repo.ArgoCDInstances.list({ name: model.name });
        //Just in case the name is an empty string or undefined, it'll return _all_ instances, so we do another filter based on the name
        const instanceSummary = instanceSummaries.Resources.find((i) => i.Name.localeCompare(model.name) === 0);
        if (instanceSummary) {
            setFoundGateway({ InstanceId: instanceSummary.Id, ...instanceSummary });
        }
        return instanceSummaries;
    }, [isWaitingForGatewayConnection, isGatewayFound, foundGateway?.HealthStatus, model.name], "Find gateway", {
        //if this is not found and the health check has not run, we want to poll every 2s
        refetchIntervalInMs: isGatewayFound && foundGateway?.HealthStatus !== "Unknown" ? undefined : 2000,
    });
    // Auto navigates to the install status page after the gateway's has being discovered
    useEffect(() => {
        if (foundGateway && !hasAutoNavigatedToStatus) {
            dialogRef.current?.moveToNextPage();
            setHasAutoNavigatedToStatus(true);
        }
    }, [foundGateway, hasAutoNavigatedToStatus]);
    // Since the dialog isn't unmounted when it's closed we have to reset the state manually
    useEffect(() => {
        if (open) {
            reset();
            setIsWaitingForGatewayConnection(false);
            setFoundGateway(undefined);
            setHasAutoNavigatedToStatus(false);
        }
    }, [open, reset]);
    //used to allow modification of URL's
    const { result: licenseResult } = useInlineStatusQuery((repo) => repo.Licenses.getCurrentStatus(), [], "Get current license status");
    const isSelfHosted = useMemo(() => licenseResult?.HostingEnvironment === HostingEnvironment.SelfHosted, [licenseResult]);
    //if the bearer token has expired, notify that it has
    useEffect(() => {
        if (bearerToken.hasExpired) {
            onBearerTokenExpired?.();
        }
    }, [bearerToken.hasExpired, onBearerTokenExpired]);
    const canRegenerateToken = bearerToken.hasExpired && !isGatewayFound;
    const handleOnClose = useCallback(() => {
        onClose(isGatewayFound);
    }, [isGatewayFound, onClose]);
    const handleConfigureProject = useCallback(() => {
        if (selectedProjectSlug) {
            dispatchProjectSelection("Submit Argo CD Instance Configure Project", {
                gatewayName: model.name,
                spaceId,
                selectionType: "Existing Project",
            });
            navigate(links.deploymentProcessEditorPage.generateUrl({ spaceId, projectSlug: selectedProjectSlug }, { stepTemplates: true, actionCategory: ActionHandlerCategory.ArgoCD }));
            onClose(true);
        }
    }, [dispatchProjectSelection, model.name, navigate, onClose, selectedProjectSlug, spaceId]);
    const handleCreateProject = useCallback(() => {
        setShowNewProjectWizard(true);
    }, []);
    const handleNewProjectWizardClose = useCallback((project: ProjectResource | undefined) => {
        setShowNewProjectWizard(false);
        if (project) {
            dispatchProjectSelection("Submit Argo CD Instance Create Project", {
                gatewayName: model.name,
                spaceId,
                selectionType: "New Project",
            });
            onClose(true);
        }
    }, [dispatchProjectSelection, model.name, onClose, spaceId]);
    const gatewayIsHealthy = foundGateway?.HealthStatus === "Healthy";
    const allowNavigatingBackToConfiguration = !isGatewayFound;
    const installationPrimaryButton = useMemo(() => {
        if (canRegenerateToken) {
            return <ActionButton label="Regenerate Token" onClick={() => bearerToken.refresh()} type={ActionButtonType.Primary}/>;
        }
    }, [bearerToken, canRegenerateToken]);
    const installationStatusPrimaryButton = useCallback((pageNavigation: PageNavigation) => {
        if (foundGateway?.HealthStatus === "ArgoCDConnectionIssues") {
            return <ActionButton label="View Gateway Logs" onClick={() => navigate(links.argoCDInstanceConnectivityPage.generateUrl({ spaceId, instanceId: foundGateway?.InstanceId }))} type={ActionButtonType.Primary}/>;
        }
        if (showProjectSelection && gatewayIsHealthy) {
            return <Button label="Next" importance="primary" onClick={async () => await pageNavigation.moveToNextPage()}/>;
        }
    }, [foundGateway?.HealthStatus, foundGateway?.InstanceId, gatewayIsHealthy, navigate, showProjectSelection, spaceId]);
    const projectSelectionPrimaryButton = useMemo(() => {
        if (useExistingProject) {
            return <Button label="Configure Project" importance="primary" disabled={!selectedProjectSlug} onClick={handleConfigureProject}/>;
        }
        return <Button label="Create Project" importance="primary" onClick={handleCreateProject}/>;
    }, [handleConfigureProject, handleCreateProject, useExistingProject, selectedProjectSlug]);
    const pages: PagedOnboardingDialogPageData[] = useMemo(() => {
        const basePages: PagedOnboardingDialogPageData[] = [
            {
                name: "Register Argo CD Instance",
                title: "Register Argo CD Instance",
                isPageModelValid: async () => await validate(),
                onMovingToNextPage: async () => bearerToken.refresh(),
                helpPanelContent: ConfigurationViewHelpContent,
                helpPanelImage: ConfigurationViewHelpImage,
                render: () => <ConfigurationView model={model} setModel={setModel} getFieldError={getFieldError} isSelfHosted={isSelfHosted}/>,
                onCloseDialog: (isCancelled) => {
                    if (isCancelled) {
                        argoCDRegistrationPageAnalyticsDispatcher("Cancel Argo CD Gateway Register Page", configurationToRegistrationEvent(model));
                    }
                },
            },
            {
                name: "Install Argo CD Gateway",
                title: "Install Argo CD Gateway",
                helpPanelContent: InstallationViewHelpContent,
                helpPanelImage: InstallationViewHelpImage,
                render: () => <InstallationView configuration={model} bearerToken={bearerToken} gateway={foundGateway}/>,
                customPrimaryButton: installationPrimaryButton,
                disableNextButton: !foundGateway,
                hideBackButton: !allowNavigatingBackToConfiguration,
                alwaysShowCloseButton: true,
                onPageDisplayed: async () => {
                    await Promise.resolve();
                    setIsWaitingForGatewayConnection(true);
                    onInstanceConfigurationChanged?.(model);
                },
            },
            {
                name: "Install Argo CD Gateway",
                title: "Install Argo CD Gateway",
                helpPanelContent: InstallationViewHelpContent,
                helpPanelImage: InstallationViewHelpImage,
                render: () => <InstallationStatusView healthStatus={foundGateway?.HealthStatus ?? "Unknown"} isFound={isGatewayFound}/>,
                customPrimaryButton: installationStatusPrimaryButton,
            },
        ];
        if (showProjectSelection) {
            basePages.push({
                name: "Configure Project",
                title: "Configure a project to deploy with Argo CD",
                helpPanelContent: ProjectSelectionViewHelpContent,
                helpPanelImage: ProjectSelectionViewHelpImage,
                render: () => <ProjectSelectionView useExistingProject={useExistingProject} onUseExistingProjectChange={setUseExistingProject} selectedProjectSlug={selectedProjectSlug} onProjectChange={setSelectedProjectSlug}/>,
                customPrimaryButton: projectSelectionPrimaryButton,
                alwaysShowCloseButton: true,
                hideBackButton: true,
                onPageDisplayed: async () => {
                    dispatchProjectSelection("View Argo CD Instance Project Selection", {
                        gatewayName: model.name,
                        spaceId,
                    });
                },
                onCloseDialog: () => {
                    dispatchProjectSelection("Dismiss Argo CD Instance Project Selection", {
                        gatewayName: model.name,
                        spaceId,
                    });
                },
            });
        }
        return basePages;
    }, [
        installationPrimaryButton,
        foundGateway,
        allowNavigatingBackToConfiguration,
        installationStatusPrimaryButton,
        showProjectSelection,
        validate,
        bearerToken,
        model,
        setModel,
        getFieldError,
        isSelfHosted,
        argoCDRegistrationPageAnalyticsDispatcher,
        onInstanceConfigurationChanged,
        isGatewayFound,
        projectSelectionPrimaryButton,
        useExistingProject,
        selectedProjectSlug,
        dispatchProjectSelection,
        spaceId,
    ]);
    return (<>
            <PagedOnboardingDialog open={open && !showNewProjectWizard} close={handleOnClose} name="Add Argo CD Instance" showPageIndicator={true} contentPanelHeight={600} pages={pages} hidePreviousButton={false} hideCompleteButton={false} helpPanelTheme={"light"} ref={dialogRef}/>
            {showNewProjectWizard && (<NewProjectWizard open={showNewProjectWizard} spaceId={spaceId} doBusyTask={doBusyTask} isOnboarding={false} redirectBehavior="skipCancel" close={handleNewProjectWizardClose} projectStatus={{ state: projectStatusState, dispatch: projectStatusDispatch }}/>)}
        </>);
};
